import { BitkitLink, BitkitTag, BitkitTagProps, IconArrowRight } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { Text } from '@chakra-ui/react/text';
import { ReactNode, useEffect } from 'react';

import Dot from '@/components/Dot/Dot';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useChangelog, { ChangelogTag } from '@/hooks/useChangelog';
import { formatDate } from '@/utils/commonFormats';

const getChangelogTopicColor = (isNew: boolean | undefined) => {
  return isNew ? 'color/purple/bold' : 'color/neutral/moderate';
};

const changelogTagMapping: Record<ChangelogTag, { title: string; color: BitkitTagProps['colorVariant'] }> = {
  // v1 used `purpleFilled`; the v2 Tag recipe has no filled variant, the subtle purple is the accepted mapping.
  'new-feature': { title: `🎉 New feature`, color: 'purple' },
  'feature-update': { title: 'Feature update', color: 'purple' },
  'step-update': { title: 'Step update', color: 'blue' },
  'api-update': { title: 'API update', color: 'green' },
  deprecation: { title: 'Deprecation', color: 'yellow' },
};

const getChangelogTag = (tags: ChangelogTag[]): ReactNode | null => {
  let tagConfig = null;
  if (tags.includes('new-feature')) tagConfig = changelogTagMapping['new-feature'];
  else if (tags.includes('feature-update')) tagConfig = changelogTagMapping['feature-update'];
  else if (tags.includes('step-update')) tagConfig = changelogTagMapping['step-update'];
  else if (tags.includes('api-update')) tagConfig = changelogTagMapping['api-update'];
  else if (tags.includes('deprecation')) tagConfig = changelogTagMapping.deprecation;
  return (
    tagConfig && (
      <>
        <BitkitTag colorVariant={tagConfig.color} labelText={tagConfig.title} size="sm" />

        <Text textStyle="body/md/regular" color="text/secondary">
          &bull;
        </Text>
      </>
    )
  );
};

const ChangelogDrawer = () => {
  const { topics: changelogTopics, isLoading, setChangelogLastVisitCookie } = useChangelog();

  useEffect(() => setChangelogLastVisitCookie(new Date()));

  return (
    <Box display="flex" flexDirection="column">
      <Text textStyle="heading/h4" paddingX="24" marginY="8">
        What&apos;s new on Bitrise
      </Text>
      {isLoading &&
        [...Array(5)].map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} overflow="hidden" paddingY="8" paddingX="24">
            <Skeleton height="24" width="80%" marginBottom="4" />
            <Skeleton height="16" width="70%" marginBottom="4" />
          </Box>
        ))}
      {changelogTopics?.slice(0, 5).map((topic) => (
        <Box
          key={topic.slug}
          asChild
          display="flex"
          gap="16"
          paddingBlock="8"
          paddingInline="24"
          _active={{ backgroundColor: 'background/active' }}
          _hover={{ backgroundColor: 'background/hover' }}
        >
          <a
            href={
              topic.id === 0
                ? `https://bitrise.io/changelog/${topic.slug}`
                : `https://bitrise.io/changelog/${topic.slug}/${topic.id}`
            }
            onClick={() => segmentTrack('Changelog Drawer Topic Name Clicked', { changelog_topic_name: topic.title })}
            rel="noreferrer noopener"
            target="_blank"
          >
            <Dot size="8" backgroundColor={getChangelogTopicColor(topic.isNew)} marginBlock="8" flexShrink="0" />
            <Box>
              <Text color="text/body" textStyle="body/lg/regular">
                {topic.title}
              </Text>
              <Box display="flex" alignItems="center" gap={5} marginTop={4}>
                {topic.tags && getChangelogTag(topic.tags)}
                <Text textStyle="body/md/regular" color="text/secondary">
                  <time dateTime={topic.created_at} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(topic.created_at, true)}
                  </time>
                </Text>
              </Box>
            </Box>
          </a>
        </Box>
      ))}
      {changelogTopics && (
        <BitkitLink
          isExternal
          href="https://bitrise.io/changelog"
          colorVariant="purple"
          marginLeft="48"
          paddingY="16"
          suffixIcon={IconArrowRight}
          suffixIconSize="16"
          onClick={() => segmentTrack('Changelog Drawer View All Button Clicked')}
        >
          View all
        </BitkitLink>
      )}
    </Box>
  );
};

export default ChangelogDrawer;
